import { useState } from "react";
import { IconButton, Stack, Typography, ButtonBase, InputBase } from "@material-ui/core";
import { useTheme, alpha } from "@material-ui/core/styles";
import type { ReactNode } from "react";

// ──────────────────────────────────────────────────────────────────────────────
// Shared "− value +" steppers.
//
// `ScaleStepper` is the single-value primitive, extracted verbatim (semantics
// intact) from the `FloorStepper` that lived at the bottom of EditContactDialog
// so the contact dialog and the listing filters stop drifting apart.
//
// Generalised in exactly one way: it walks an explicit ASCENDING `scale` array
// instead of assuming ±1 integers. That is what lets rooms use the domain's
// half steps (1, 1.5, 2, …) and floors use its sparse tail (…20, 25, 30, …)
// without either site hardcoding its own arithmetic.
//
// `ScaleRangeStepper` composes two of them into a min/max pair and owns the
// cross-bound auto-correction, so a caller can never end up with min > max.
// ──────────────────────────────────────────────────────────────────────────────

/** Index of `value` on the scale; nearest entry when the value is off-scale
 *  (legacy/imported data), so a stepper always has somewhere to step from. */
export function indexOnScale(scale: Readonly<number[]>, value: number): number {
  const exact = scale.indexOf(value);
  if (exact !== -1) return exact;

  let nearest = 0;
  for (let i = 1; i < scale.length; i += 1) {
    if (Math.abs(scale[i] - value) < Math.abs(scale[nearest] - value)) nearest = i;
  }
  return nearest;
}

export type ScaleStepperProps = {
  /** Ascending list of every value this stepper may take. */
  scale: Readonly<number[]>;
  /** `null` renders the "unset / any" state — a real, expressible value. */
  value: number | null;
  /**
   * The OTHER bound of a range. An empty stepper's first tap materialises this
   * instead of jumping to a scale end, so with min=3 the first "+" on max shows
   * 3 rather than the scale floor (which would immediately drag min down).
   */
  startFrom?: number | null;
  /**
   * What an empty stepper with no `startFrom` materialises on first tap.
   * Defaults to the scale ends. EditContactDialog's floor steppers pass
   * `{ up: 0, down: -1 }` to preserve their original "ground floor up /
   * basement down" seeding.
   */
  emptySeed?: { up: number; down: number };
  disabled?: boolean;
  /** Committing an EMPTY typed value clears back to `null`. On by default. */
  clearable?: boolean;
  /** Render "6+", "50+", … instead of the bare number. */
  formatValue?: (value: number) => ReactNode;
  label?: string;
  onChange: (value: number | null) => void;
};

export function ScaleStepper({
  scale,
  value,
  startFrom,
  emptySeed,
  disabled = false,
  clearable = true,
  formatValue,
  label,
  onChange
}: ScaleStepperProps): JSX.Element {
  const theme = useTheme();

  // Inline typing (design feedback 2026-08): clicking the value USED to clear
  // it — surprising and destructive. It now opens a small input instead;
  // committing an empty input is the (deliberate) way to clear.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const seedUp = emptySeed?.up ?? scale[0];
  const seedDown = emptySeed?.down ?? scale[scale.length - 1];
  const currentIndex = value == null ? -1 : indexOnScale(scale, value);

  const startEditing = (): void => {
    if (disabled) return;
    setDraft(value == null ? "" : String(value));
    setEditing(true);
  };

  const commitDraft = (): void => {
    setEditing(false);
    const trimmed = draft.trim();
    if (!trimmed) {
      // Empty commit = clear (when allowed). A no-op when already unset.
      if (clearable && value != null) onChange(null);
      return;
    }
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) return; // garbage input — keep the old value
    // Snap onto the scale: the stepper must never emit a value the filter
    // cannot represent (half-room steps, the sparse floor tail, …).
    onChange(scale[indexOnScale(scale, parsed)]);
  };

  // At a scale end the button is DISABLED rather than silently doing nothing,
  // so "nothing happened" is never ambiguous. An empty stepper is never at an
  // end — its first tap materialises a value instead of moving.
  const atTop = value != null && currentIndex >= scale.length - 1;
  const atBottom = value != null && currentIndex <= 0;

  const bump = (direction: 1 | -1) => (): void => {
    if (value == null) {
      // Snap the materialised value onto the scale: `startFrom` may be the other
      // bound of a legacy range that is off-scale or out of bounds, and a
      // stepper must never emit a value the filter cannot represent.
      const seed = startFrom ?? (direction > 0 ? seedUp : seedDown);
      onChange(scale[indexOnScale(scale, seed)]);
      return;
    }
    const nextIndex = Math.max(0, Math.min(scale.length - 1, currentIndex + direction));
    onChange(scale[nextIndex]);
  };

  const stepButtonSx = {
    borderRadius: "9px",
    bgcolor: alpha(theme.palette.grey[500], 0.12),
    width: 30,
    height: 30,
    fontSize: 18,
    fontWeight: 700
  } as const;

  return (
    <Stack spacing={0.5} alignItems="center">
      {label && (
        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
          {label}
        </Typography>
      )}
      <Stack
        role="group"
        aria-label={label}
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{
          border: "1.5px solid",
          borderColor: alpha(theme.palette.grey[500], 0.3),
          borderRadius: "12px",
          p: 0.5
        }}
      >
        <IconButton
          size="small"
          aria-label={`${label ?? ""} הפחתה`.trim()}
          disabled={disabled || atBottom}
          onClick={bump(-1)}
          sx={stepButtonSx}
        >
          −
        </IconButton>

        {editing ? (
          <InputBase
            autoFocus
            value={draft}
            // A number is LTR content: without this a negative floor renders "1-".
            dir="ltr"
            inputProps={{
              inputMode: "decimal",
              "aria-label": `${label ?? ""} הקלדת ערך`.trim(),
              style: { textAlign: "center", fontWeight: 800, fontSize: 15, padding: 0 }
            }}
            onFocus={(event) => event.target.select()}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={commitDraft}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                commitDraft();
              }
              if (event.key === "Escape") {
                // Cancel the edit WITHOUT letting Escape bubble on to close the
                // hosting drawer/dialog.
                event.stopPropagation();
                setEditing(false);
              }
            }}
            sx={{ width: 48 }}
          />
        ) : (
          <ButtonBase
            disabled={disabled}
            onClick={startEditing}
            // A number is LTR content: without this a negative floor renders "1-".
            dir="ltr"
            title="הקלדת ערך"
            aria-label={`${label ?? ""} עריכה`.trim()}
            sx={{
              minWidth: 40,
              justifyContent: "center",
              fontSize: 15,
              fontWeight: 800,
              color: value == null ? "text.disabled" : "text.primary",
              borderRadius: 1
            }}
          >
            {value == null ? "—" : formatValue?.(value) ?? value}
          </ButtonBase>
        )}

        <IconButton
          size="small"
          aria-label={`${label ?? ""} הוספה`.trim()}
          disabled={disabled || atTop}
          onClick={bump(1)}
          sx={stepButtonSx}
        >
          +
        </IconButton>
      </Stack>
    </Stack>
  );
}

export type ScaleRangeStepperProps = {
  scale: Readonly<number[]>;
  min: number | null;
  max: number | null;
  disabled?: boolean;
  minLabel?: string;
  maxLabel?: string;
  formatValue?: (value: number) => ReactNode;
  /**
   * What an EMPTY bound materialises on its first tap, forwarded to both steppers.
   *
   * ⚠️ WITHOUT IT THE FIRST TAP JUMPS TO A SCALE END, which is only harmless when the
   * ends are sane. `rooms` starts at 1, so "+" on an empty room bound gives 1 and nobody
   * notices. `floors` starts at −4: "+" on an empty floor bound gives **מרתף 4**, and "−"
   * gives the 50th storey. The single {@link ScaleStepper} has always taken this prop —
   * its own doc names EditContactDialog's floor steppers as the caller that passes
   * `{ up: 0, down: -1 }` — it simply was not plumbed through the pair until a second
   * floor control needed it.
   */
  emptySeed?: { up: number; down: number };
  onChange: (min: number | null, max: number | null) => void;
};

/**
 * Min/max pair that can never be inverted.
 *
 * Auto-correction (the whole point):
 *  - raising min above max pushes max up to meet it;
 *  - lowering max below min pulls min down to meet it;
 *  - either bound may be cleared to `null` ("any") independently — an unset
 *    bound constrains nothing, so it never triggers a correction.
 */
export function ScaleRangeStepper({
  scale,
  min,
  max,
  disabled,
  minLabel,
  maxLabel,
  formatValue,
  emptySeed,
  onChange
}: ScaleRangeStepperProps): JSX.Element {
  const handleMin = (next: number | null): void => {
    if (next != null && max != null && next > max) onChange(next, next);
    else onChange(next, max);
  };

  const handleMax = (next: number | null): void => {
    if (next != null && min != null && next < min) onChange(next, next);
    else onChange(min, next);
  };

  return (
    <Stack direction="row" alignItems="flex-end" justifyContent="center" spacing={2}>
      <ScaleStepper
        scale={scale}
        value={min}
        startFrom={max}
        emptySeed={emptySeed}
        disabled={disabled}
        label={minLabel}
        formatValue={formatValue}
        onChange={handleMin}
      />
      <ScaleStepper
        scale={scale}
        value={max}
        startFrom={min}
        emptySeed={emptySeed}
        disabled={disabled}
        label={maxLabel}
        formatValue={formatValue}
        onChange={handleMax}
      />
    </Stack>
  );
}
